<script lang="ts">
  import { Card, Checkbox, Helper } from "flowbite-svelte";
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
  import { createEventDispatcher } from "svelte";

  type ValidFields = "customers";

  interface Customer {
    customer_id: string;
    name: string;
    status?: string;
  }

  export let userData: any;
  export let lookups: any;

  const dispatch = createEventDispatcher();
  
  let errors: Record<ValidFields, string> = {
    customers: ""
  };

  let selectedCustomers: Customer[] = [];

  function validateCustomers() {
    if (isOHUserAdmin) {
      // OH admin users don't need customer validation
      errors.customers = "";
      dispatch('customerSelect', {
        customerId: "all",
        checked: true
      });
    } else if (isCustomerUser) {
      // Customer users must have exactly one customer
      errors.customers = selectedCustomers.length !== 1 ? "Customer selection required" : "";
    } else {
      // Other users must have at least one customer
      errors.customers = selectedCustomers.length === 0 ? "At least one customer required" : "";
    }
    errorState = { ...errors };
  }

  // Initialize selectedCustomers when userData changes
  $: {
    if (isOHUserAdmin) {
      selectedCustomers = lookups?.customer?.list || [];
    } else if (isCustomerUser && userData?.customers?.length > 0) {
      selectedCustomers = [userData.customers[0]];
    } else {
      selectedCustomers = userData?.customers || [];
    }
    validateCustomers();
  }

  $: isCustomerUser = userData?.user_type === "customer";
  $: isOHUserAdmin = userData?.user_type === "oh-user" && userData?.user_role === "admin";

  $: errorState = errors;

  function isCustomerSelected(customerId: string) {
    return selectedCustomers.some((customer: Customer) => customer.customer_id === customerId);
  }

  function handleCustomerSelection(customerId: string, checked: boolean) {
    if (isOHUserAdmin) return;

    const customerData = lookups.customer.list.find((c: Customer) => c.customer_id === customerId);
    if (!customerData) return;

    if (isCustomerUser) {
      selectedCustomers = checked ? [customerData] : [];
    } else {
      if (checked) {
        selectedCustomers = [...selectedCustomers, customerData];
      } else {
        selectedCustomers = selectedCustomers.filter((customer: Customer) => customer.customer_id !== customerId);
      }
    }

    validateCustomers();
    dispatch('customerSelect', {
      customerId: customerId,
      checked: checked
    });
  }

  function handleCheckboxChange(customerId: string) {
    return function(event: any) {
      handleCustomerSelection(customerId, event.target.checked);
    }
  }

  function handleSave() {
    dispatch('save', {
      ...userData,
      customers: selectedCustomers
    });
  }
</script>

<Card class="app-mutation-area max-w-sm flex flex-col">
  <div>
    <div class="pl-2 pr-2">
      <div class="">
        <h4 class="card-label-text">
          Customer Assignment {#if !isOHUserAdmin}<FontAwesomeIcon icon={faAsterisk} class="ml-1 text-red-400" title="Required field" />{/if}
        </h4>
        {#if isCustomerUser}
          <Helper class="helper-text mb-1">Select the customer assigned to this user.</Helper>
        {:else}
          {#if isOHUserAdmin}
            <Helper class="helper-text mb-1">OH Admin has all customers assigned automatically.</Helper>
          {:else}
            <Helper class="helper-text mb-1">Select all customers assigned to this user.</Helper>
          {/if}
        {/if}
        <div class="space-y-2">
          {#each lookups.customer.list as customer}
            <div class="flex items-center">
              {#if isCustomerUser}
                <Checkbox
                  id="customer-{customer.customer_id}"
                  class="card-checkbox {errorState.customers ? 'error' : ''}"
                  value={customer.customer_id}
                  checked={selectedCustomers[0]?.customer_id === customer.customer_id}
                  on:change={handleCheckboxChange(customer.customer_id)}
                >
                  {customer.name}
                </Checkbox>
              {:else}
                <Checkbox
                  class="card-checkbox {errorState.customers ? 'error' : ''}"
                  disabled={isOHUserAdmin}
                  checked={isCustomerSelected(customer.customer_id)}
                  on:change={handleCheckboxChange(customer.customer_id)}
                >
                  {customer.name}
                </Checkbox>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
</Card>
